import React from 'react';
import filter from 'lodash/filter';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import { Loading, ProductDetail } from '@commonsku/styles';
import { usePoDProduct } from './usePoDProduct';
import { buildSizePriceColumns } from './podPricing';
import { sizeSort } from '../../utils';

interface PoDProductDetailsProps {
  extProductId: string;
  handleClickProductEvent: () => void;
  selected: boolean;
  showAddButton: boolean;
  divisionName?: string;
  divisionHref?: string;
  addToTarget?: string;
}

const PoDProductDetails: React.FC<PoDProductDetailsProps> = ({
  extProductId,
  handleClickProductEvent,
  selected,
  showAddButton,
  divisionName,
  divisionHref,
  addToTarget,
}) => {
  const { product, loading, error } = usePoDProduct(extProductId);

  if (loading) {
    return <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}><Loading /></div>;
  }

  if (error || !product) {
    return <div style={{ padding: 16, color: '#B90000' }}>Unable to load product details.</div>;
  }

  const sizes = filter(uniq(map(product.skus, 'size'))).sort((a, b) => sizeSort(a, b));
  const allColors = filter(uniq(map(product.skus, 'color')));
  const images = product.images.flatMap((image) =>
    image.url ? [{ url: image.url }] : [],
  );
  // The price table is size-based (not quantity-based): each column is a size
  // range with its surcharged price. ProductDetail renders the column labels
  // as-is, so size labels work in place of quantities.
  const prices = buildSizePriceColumns(product.skus)
    .map((column) => ({
      min_quantity: column.label as unknown as number,
      price: String(column.price),
    }));

  return (
    <ProductDetail
      name={product.name}
      sku={product.pod_product_id}
      descriptions={product.description ?? ''}
      divisionName={divisionName}
      divisionHref={divisionHref}
      productHref=""
      sizes={sizes}
      allColors={allColors}
      images={images}
      mainImage={images[0]}
      prices={prices}
      handleClickProductEvent={handleClickProductEvent}
      selected={selected}
      showAddButton={showAddButton}
      addToTarget={addToTarget}
      priceTableLabel='Size'
    />
  );
};

export default PoDProductDetails;
